import {
  holidayColor,
  nonWorkdayColor,
  otherColor,
  publicHolidayColor,
  sickColor,
  workdayColor,
} from './colorConfig';
import {
  Button,
  Input,
  Option,
  Select,
  Text,
} from '../../common/components/defaultUIComponents';
import TimetrackingApi, {
  OffDayType,
} from '../../common/api/zeiterfassungServerApi/TimetrackingApi';
import { Box, Flex, IconButton, Stack, useToast } from '@chakra-ui/react';
import useTranslate, { useMonthAndYear } from '~/common/utils/translate';
import { useTimetrackingStore } from './utils';
import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import { LegendBadge } from './LegendBadgeProps';
import { useCallback, useState } from 'react';
import _ from 'lodash';

export const TimetrackingToolbar = () => {
  const t = useTranslate();
  const toast = useToast();
  const [type, setType] = useState<OffDayType>('HOLIDAY');
  const [name, setName] = useState('');
  const getMonthAndYear = useMonthAndYear();
  const { selectedDays, currentDate, nextMonth, prevMonth } =
    useTimetrackingStore();

  const { mutateAsync: addOffDaysAsync } = TimetrackingApi.hooks.useAddOffDay();
  const { mutateAsync: deleteOffDaysAsync } =
    TimetrackingApi.hooks.useDeleteOffDays();

  const addOffDays = useCallback(async () => {
    const request = {
      selectedDays: selectedDays,
      type,
      name,
    };

    await addOffDaysAsync(request);
  }, [selectedDays, name, type]);

  const deleteOffDays = useCallback(() => {
    return deleteOffDaysAsync({ selectedDays });
  }, [selectedDays]);

  return (
    <Stack>
      <Flex justify="space-between">
        <Flex>
          <Select
            pl={9}
            mr={5}
            onChange={(e) => setType(e.target.value as OffDayType)}
            value={type}
            name="type"
            placeholder="timeTracking.offDayToolbar.type"
            borderWidth={1}
            borderColor="#807a7a"
          >
            <Option value="HOLIDAY">
              timeTracking.addOffDayPopver.holiday
            </Option>
            <Option value="SICK">timeTracking.addOffDayPopver.sick</Option>
            <Option value="PUBLIC_HOLIDAY">
              timeTracking.addOffDayPopver.publicHoliday
            </Option>
            <Option value="OTHER">timeTracking.addOffDayPopver.other</Option>
          </Select>
          <Box maxW="50%" mr={7} mb={5}>
            <Input
              onChange={(e) => setName(e.target.value)}
              value={name}
              name="name"
              label="timeTracking.offDayToolbar.name"
              labelAsPlaceholder
              width="92%"
            />
          </Box>
          <Button
            isDisabled={!type}
            onClick={() =>
              void addOffDays().catch(() =>
                toast({
                  status: 'error',
                  description: t('generic.error'),
                }),
              )
            }
            mr={2}
            variant="submit"
          >
            timetracking.offDayToolbar.add
          </Button>
          <Button
            isDisabled={selectedDays?.length === 0}
            onClick={() => {
              deleteOffDays().catch(() =>
                toast({
                  status: 'error',
                  description: t('generic.error'),
                }),
              );
            }}
            variant="danger"
          >
            timetracking.offDayToolbar.delteOffDays
          </Button>
          <Flex direction="column" ml={8} mb={5}>
            <Flex align="center" mb={2}>
              {' '}
              <Flex align="center" mr={4} width="110px">
                {' '}
                <LegendBadge color={publicHolidayColor} />
                <Text fontSize="large" px={1}>
                  {t('timeTracking.legend.publicHoliday')}
                </Text>
              </Flex>
              <Flex align="center" mr={4} width="125px">
                {' '}
                <LegendBadge color={holidayColor} />
                <Text fontSize="large" px={1}>
                  {t('timeTracking.legend.holiday')}
                </Text>
              </Flex>
              <Flex align="center" width="160px">
                {' '}
                <LegendBadge color={sickColor} />
                <Text fontSize="large" px={1}>
                  {t('timeTracking.legend.sick')}
                </Text>
              </Flex>
            </Flex>
            <Flex align="center">
              {' '}
              <Flex align="center" mr={4} width="110px">
                {' '}
                <LegendBadge color={otherColor} />
                <Text fontSize="large" px={1}>
                  {t('timeTracking.legend.other')}
                </Text>
              </Flex>
              <Flex align="center" mr={4} width="125px">
                {' '}
                <LegendBadge color={workdayColor} />
                <Text fontSize="large" px={1}>
                  {t('timeTracking.legend.working')}
                </Text>
              </Flex>
              <Flex align="center" width="160px">
                {' '}
                <LegendBadge color={nonWorkdayColor} />
                <Text fontSize="large" px={1}>
                  {t('timeTracking.legend.not_working')}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Flex direction="row">
          <Text
            fontSize="2xl"
            mr={2}
            textDecoration="underline"
            fontWeight="semibold"
          >
            {getMonthAndYear(currentDate)}
          </Text>
          <IconButton
            icon={<ArrowLeftIcon />}
            onClick={() => prevMonth()}
            aria-label="previous-month"
            isRound
          />
          <IconButton
            icon={<ArrowRightIcon />}
            onClick={() => nextMonth()}
            aria-label="next-month"
            isRound
          />
        </Flex>
      </Flex>
    </Stack>
  );
};
